import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import KycDetailModal from './KycDetailModal';
import KycEditModal from './KycEditModal';
import KycAuditModal from './KycAuditModal';
import KycJourneyModal from './KycJourneyModal';
import {
  MEMBER_ROLE_LABELS,
  MEMBER_ROLE_OPTIONS,
  KYC_CERT_TYPE_FILTER_OPTIONS,
  REAL_NAME_STATUS_OPTIONS,
  buildKycPageBody,
  canAuditKyc,
  formatCertType,
  formatGender,
  formatOptionalId,
  formatRealNameStatus,
  kycStatusClassName,
  statusCode,
  validateKycUpdateTimeRange,
} from './kycConstants';

const PAGE_SIZE = 20;

const EMPTY_FILTERS = {
  roleId: '',
  realNameStatus: '',
  memberId: '',
  inviterId: '',
  topInviterId: '',
  phoneEmail: '',
  lastName: '',
  realName: '',
  identityNumber: '',
  certType: '',
  updateTimeFromInput: '',
  updateTimeToInput: '',
};

const TABLE_COLUMNS = [
  '頂級上級用戶ID',
  '直屬上級用戶ID',
  '用戶ID',
  '帳號類型',
  '手機號碼',
  '電子信箱',
  '姓',
  '名',
  '性別',
  '證件號碼',
  '證件類型',
  '認證狀態',
  '操作',
];

export default function KycIdentityVerificationPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailMemberId, setDetailMemberId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [auditRow, setAuditRow] = useState(null);
  const [journeyMemberId, setJourneyMemberId] = useState(null);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.kycPage(pageNo, PAGE_SIZE, buildKycPageBody(nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validateKycUpdateTimeRange(
      filters.updateTimeFromInput,
      filters.updateTimeToInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setError('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setError('');
    loadRecords(1, reset);
  }

  function handleSaved() {
    loadRecords(page);
  }

  async function openEditFromDetail(detail) {
    setDetailMemberId(null);
    if (detail?.memberId) {
      try {
        const fresh = await adminApi.kycDetail(detail.memberId);
        setEditRow(fresh || detail);
      } catch {
        setEditRow(detail);
      }
    }
  }

  return (
    <div className="page-card">
      <h2>KYC 身份認證</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="kycRoleId">帳號類型</label>
          <select
            id="kycRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {MEMBER_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="kycStatus">身份認證狀態</label>
          <select
            id="kycStatus"
            value={filters.realNameStatus}
            onChange={(e) => updateFilter('realNameStatus', e.target.value)}
          >
            {REAL_NAME_STATUS_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="kycMemberId">用戶ID</label>
          <input
            id="kycMemberId"
            type="number"
            value={filters.memberId}
            onChange={(e) => updateFilter('memberId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="kycInviterId">直屬上級用戶ID</label>
          <input
            id="kycInviterId"
            type="number"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="kycTopInviterId">頂級上級用戶ID</label>
          <input
            id="kycTopInviterId"
            type="number"
            value={filters.topInviterId}
            onChange={(e) => updateFilter('topInviterId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="kycPhoneEmail">用戶郵箱/手機號</label>
          <input
            id="kycPhoneEmail"
            type="text"
            value={filters.phoneEmail}
            onChange={(e) => updateFilter('phoneEmail', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="kycLastName">用戶姓</label>
          <input
            id="kycLastName"
            type="text"
            value={filters.lastName}
            onChange={(e) => updateFilter('lastName', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="kycRealName">用戶名</label>
          <input
            id="kycRealName"
            type="text"
            value={filters.realName}
            onChange={(e) => updateFilter('realName', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="kycIdentityNumber">證件號碼</label>
          <input
            id="kycIdentityNumber"
            type="text"
            value={filters.identityNumber}
            onChange={(e) => updateFilter('identityNumber', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="kycCertType">證件類型</label>
          <select
            id="kycCertType"
            value={filters.certType}
            onChange={(e) => updateFilter('certType', e.target.value)}
          >
            {KYC_CERT_TYPE_FILTER_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="kycUpdateTimeFrom">更新時間起</label>
          <input
            id="kycUpdateTimeFrom"
            type="datetime-local"
            value={filters.updateTimeFromInput}
            onChange={(e) => updateFilter('updateTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="kycUpdateTimeTo">更新時間迄</label>
          <input
            id="kycUpdateTimeTo"
            type="datetime-local"
            value={filters.updateTimeToInput}
            onChange={(e) => updateFilter('updateTimeToInput', e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => {
                const status = statusCode(row.realNameStatus);
                return (
                  <tr key={row.memberId || row.id}>
                    <td>{formatOptionalId(row.topInviterId)}</td>
                    <td>{formatOptionalId(row.inviterId)}</td>
                    <td className="mono-cell">{formatOptionalId(row.memberId)}</td>
                    <td>{MEMBER_ROLE_LABELS[row.roleId] || row.roleId || '—'}</td>
                    <td>{row.mobile || '—'}</td>
                    <td>{row.email || '—'}</td>
                    <td>{row.lastName || '—'}</td>
                    <td>{row.realName || '—'}</td>
                    <td>{formatGender(row.gender)}</td>
                    <td className="mono-cell">{row.identityNumber || '—'}</td>
                    <td>{formatCertType(row.certType)}</td>
                    <td>
                      <span className={kycStatusClassName(status)}>
                        {formatRealNameStatus(status)}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions table-actions--stack">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setDetailMemberId(row.memberId)}
                        >
                          詳情
                        </button>
                        {canAuditKyc(row) && (
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={() => setAuditRow(row)}
                          >
                            KYC審核
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        onChange={(p) => loadRecords(p, filters)}
      />

      <KycDetailModal
        open={Boolean(detailMemberId)}
        memberId={detailMemberId}
        onClose={() => setDetailMemberId(null)}
        onEdit={openEditFromDetail}
        onJourney={(memberId) => {
          setDetailMemberId(null);
          setJourneyMemberId(memberId);
        }}
      />

      <KycEditModal
        open={Boolean(editRow)}
        row={editRow}
        onClose={() => setEditRow(null)}
        onSaved={handleSaved}
      />

      <KycAuditModal
        open={Boolean(auditRow)}
        row={auditRow}
        onClose={() => setAuditRow(null)}
        onSaved={handleSaved}
      />

      <KycJourneyModal
        open={Boolean(journeyMemberId)}
        memberId={journeyMemberId}
        onClose={() => setJourneyMemberId(null)}
      />
    </div>
  );
}
